import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Container,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PlanKey } from '../../pages/registration/homeowner-plan-selection-fieldset';
import './styles.scss';

interface PaymentGateProps {
  selectedPlan: PlanKey;
  onPaymentSuccess: () => void;
  children: React.ReactNode;
}

const PAYMENT_LINKS: Record<PlanKey, string> = {
  Guild: 'https://crm.homedesignguild.com/payment-link/68dd83dbbe6db40b86e7add3',
  GuildPlus: 'https://crm.homedesignguild.com/payment-link/68dd861967ee3b0162698aea',
  basic: '', // No payment link for basic plan
};

const PLAN_DETAILS: Record<PlanKey, {
  name: string;
  price: string;
  period: string;
  description: string;
}> = {
  Guild: {
    name: 'Guild',
    price: '$4.99',
    period: 'per month',
    description: 'Ready to see what we are all about? The Guild level gives you full access to our detailed real-world projects from our vetted designers, along with full access to our educational articles.',
  },
  GuildPlus: {
    name: 'Guild+',
    price: '$149.99',
    period: 'one-time payment',
    description: 'Ready to find your designer match? Enjoy all the perks of Guild level while also getting paired with a selection of highly compatible designers, who "get" you!',
  },
  basic: {
    name: 'Basic',
    price: 'Free',
    period: 'forever',
    description: 'Basic access to Home Design Guild features.',
  }
};

export const PaymentGate: React.FC<PaymentGateProps> = ({
  selectedPlan,
  onPaymentSuccess,
  children,
}) => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentWindow, setPaymentWindow] = useState<Window | null>(null);

  const planDetails = PLAN_DETAILS[selectedPlan];
  const paymentLink = PAYMENT_LINKS[selectedPlan];

  // Handle payment window messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Check if message is from HighLevel payment window
      if (event.origin !== 'https://crm.homedesignguild.com') return;
      
      if (event.data.type === 'PAYMENT_SUCCESS') {
        setIsProcessing(false);
        setPaymentError(null);
        onPaymentSuccess();
      } else if (event.data.type === 'PAYMENT_FAILURE') {
        setIsProcessing(false);
        setPaymentError(event.data.message || 'Payment failed. Please try again.');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onPaymentSuccess]);

  // Check for payment success in URL params (fallback)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    
    if (paymentStatus === 'success') {
      onPaymentSuccess();
    } else if (paymentStatus === 'failure') {
      setPaymentError('Payment was not completed successfully.');
    }
  }, [onPaymentSuccess]);

  const handlePayment = () => {
    if (!paymentLink) {
      setPaymentError('Payment link not available. Please try again.');
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    // Open payment window
    const windowFeatures = 'width=800,height=600,scrollbars=yes,resizable=yes';
    const newWindow = window.open(paymentLink, 'payment', windowFeatures);
    
    if (!newWindow) {
      setPaymentError('Unable to open payment window. Please check your popup blocker.');
      setIsProcessing(false);
      return;
    }

    setPaymentWindow(newWindow);

    // Check if window is closed manually
    const checkClosed = setInterval(() => {
      if (newWindow.closed) {
        clearInterval(checkClosed);
        setIsProcessing(false);
        // Don't automatically fail - user might have completed payment in another tab
      }
    }, 1000);
  };

  const handleContactSupport = () => {
    navigate('/contact');
  };

  // If it's a free plan, show children immediately
  if (selectedPlan === 'basic') {
    return <>{children}</>;
  }

  return (
    <Box className="payment-gate">
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" className="font-title" sx={{ mb: 2, color: '#1a1a1a' }}>
            Payment Required
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', maxWidth: '600px', mx: 'auto' }}>
            Complete your payment to access all the features of your {planDetails.name} membership.
          </Typography>
        </Box>

        <Card sx={{ mb: 4, boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h5" className="font-title" sx={{ mb: 1, color: '#d22268' }}>
                {planDetails.name}
              </Typography>
              <Typography variant="h3" className="font-title" sx={{ mb: 1, color: '#1a1a1a' }}>
                {planDetails.price}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                {planDetails.period}
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: '#333' }}>
              {planDetails.description}
            </Typography>

            {paymentError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {paymentError}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handlePayment}
                disabled={isProcessing}
                sx={{
                  backgroundColor: '#d22268',
                  color: 'white',
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  minWidth: '250px',
                  '&:hover': {
                    backgroundColor: '#b51b57',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#ddd',
                    color: '#888',
                  },
                }}
              >
                {isProcessing ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: '#00A06D' }} />
                    Processing...
                  </>
                ) : (
                  `Pay ${planDetails.price}`
                )}
              </Button>

              <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', maxWidth: '400px' }}>
                By completing payment, you agree to our Terms of Service and Privacy Policy. 
                You can cancel your subscription at any time.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Having trouble with payment?
          </Typography>
          <Button
            variant="outlined"
            onClick={handleContactSupport}
            sx={{
              borderColor: '#d22268',
              color: '#d22268',
              '&:hover': {
                borderColor: '#b51b57',
                backgroundColor: 'rgba(210, 34, 104, 0.04)',
              },
            }}
          >
            Contact Support
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
